import { styled } from "@schaeffler-commerce/design-system";
import type { PageTemplateProps } from "./pageTemplateTypes";

/**
 * Migrated from styles/jsp/pages/product-page-group.less: desktop 20px block
 * padding; below desktop-s the content goes edge-to-edge (padding stripped with
 * the original !important) with 32px top margin and 32px slot spacing for the
 * SmartEdit yCmsContentSlot wrappers. The mobile bold overrides for
 * .jsp__image-header__header/.jsp__tile-component__header died with those class
 * families; the tile bold lives on in CMSImageTile's TileHeading
 * (.scha__product-group-page__content &).
 */
const ProductGroupContent = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5),
  [theme.breakpoints.down("desktop-s")]: {
    marginTop: theme.spacing(4),
    padding: `0 !important`,
    "& .yCmsContentSlot": {
      marginBottom: `${theme.spacing(4)} !important`,
    },
  },
}));

/**
 * Slot-driven React template for the catch-all Product Group page whose backend
 * {@code metadata.templateUid} is {@code "ProductGroupPageTemplate"} (legacy
 * {@code product/productGroupPage.jsp}).
 *
 * Structure-copy parity of {@code productGroupPage.jsp}: a {@code scha__product-group-page}
 * shell with a {@code __main} region (full-width {@code Section1}) and a {@code __content}
 * region ({@code Section2}–{@code Section4}). The {@code __content} class must stay on the
 * region: CMSImageTile's TileHeading bolds tile headings inside it below desktop-s.
 * {@code span-24} and {@code mb-4} are the JSP slot wrapper classes.
 *
 * The JSP renders only {@code Section1}–{@code Section4}. CMS also declares
 * {@code Section5}/{@code Section6} for this template, but the legacy JSP never placed
 * them, so they are intentionally NOT rendered here to avoid surfacing content that the
 * JSP hid (mirroring the JSP structure, per the 0153 parity rule).
 *
 * Catch-all product-group pages ({@code /productGroup}, {@code /ball-bearings}) have no
 * React island, so {@code body} is null.
 */
export function ProductGroupPageTemplate({ slots, body }: PageTemplateProps) {
  return (
    <div className="js-jsp__page-pgp scha__product-group-page">
      <div className="scha__product-group-page__main">
        <div className="span-24">{slots.renderSlot("Section1")}</div>
      </div>
      <ProductGroupContent className="scha__product-group-page__content">
        <div className="mb-4">{slots.renderSlot("Section2")}</div>
        <div className="mb-4">{slots.renderSlot("Section3")}</div>
        <div className="mb-4">{slots.renderSlot("Section4")}</div>
        {body}
      </ProductGroupContent>
    </div>
  );
}
